import { useEffect, useState } from 'react';
import { useLocale, useQuery, type Option } from 'shared-ui';
import { FormProvider, useForm } from 'react-hook-form';

import { useModalState } from 'hooks/useModalState/useModalState';
import { fetchServers } from 'api/servers/servers';
import { QueryKeyEnum } from 'core/global.enum';
import { fetchTerminals } from 'api/terminals/terminals';
import { GroupTerminalsPermissionsIdSortEnum } from 'context/query/queryContext/QueryContext.enum';
import { AppMessages } from 'i18n/messages';
import { downloadPermissionsSummaryReport } from 'api/reports/reports';
import { useReportDownload } from '../hooks/useReportDownload';
import { useSmtpConfiguration } from '../hooks/useSmtpConfiguration';
import { EmailSelectionModalContainer } from '../emailSelectionModal';

import { PermissionsSummaryReportForm } from './PermissionsSummaryReport.types';
import { PermissionsSummaryReport } from './PermissionsSummaryReport';

export const PermissionsSummaryReportContainer = () => {
  const { isModalOpen, onModalClose, onModalOpen } = useModalState();
  const {
    isModalOpen: isEmailModalOpen,
    onModalClose: onEmailModalClose,
    onModalOpen: onEmailModalOpen,
  } = useModalState();
  const { formatMessage } = useLocale();
  const [selectedEmails, setSelectedEmails] = useState<Option[]>([]);
  const { isSmtpConfigured } = useSmtpConfiguration();

  const formMethods = useForm<PermissionsSummaryReportForm>({
    defaultValues: {
      blocked: 'true',
    },
  });

  const { nazwisko, numerKarty, blocked, komorka, tkds, serwerId } = formMethods.watch();

  const { data: serversResponse } = useQuery([QueryKeyEnum.SERVERS], fetchServers);
  const servers = serversResponse?.data || [];

  const { data: terminalsResponse, isInitialLoading: isLoadingTerminals } = useQuery(
    [QueryKeyEnum.TERMINALS, serwerId],
    () => fetchTerminals(serwerId),
    {
      args: {
        sort: [GroupTerminalsPermissionsIdSortEnum.ASC],
      },
      enabled: !!serwerId,
    },
  );
  const terminals = terminalsResponse?.data || [];

  const {
    onDownload: onDownloadPermissionsSummaryReport,
    isLoading: isLoadingDownload,
    hasNoDataError,
    resetError,
  } = useReportDownload({
    mutationFn: downloadPermissionsSummaryReport,
    buildRequestBody: (emails) => ({
      nazwisko,
      numerKarty,
      blocked: blocked === 'true' ? true : false,
      komorka: komorka?.id ? [komorka?.id] : [],
      tkds: tkds?.map((tkd) => tkd.value.toString()),
      selectedEmails: emails,
    }),
    onEmailModalClose,
    reportNameId: AppMessages['reports.permissionsSummary.title'],
  });

  const handleEmailModalOpen = () => {
    resetError();
    onEmailModalOpen();
  };

  const handleConfirmEmails = (emails: string[]) => {
    // Save selected emails for persistence
    setSelectedEmails(emails.map((email) => ({ value: email, label: email })));
    // Trigger the download/send
    onDownloadPermissionsSummaryReport(emails);
  };

  useEffect(() => {
    if (isModalOpen) {
      formMethods.reset({
        blocked: 'false',
        serwerId: servers[0]?.id,
      });
    } else {
      // Reset error and selected emails when modal closes
      resetError();
      setSelectedEmails([]);
    }
  }, [isModalOpen]);

  return (
    <FormProvider {...formMethods}>
      <PermissionsSummaryReport
        servers={servers}
        isModalOpen={isModalOpen}
        onModalClose={onModalClose}
        onModalOpen={onModalOpen}
        terminals={terminals}
        isLoadingTerminals={isLoadingTerminals}
        onDownload={onDownloadPermissionsSummaryReport}
        isLoadingDownload={isLoadingDownload}
        hasNoDataError={hasNoDataError}
        onEmailModalOpen={handleEmailModalOpen}
        isEmailModalOpen={isEmailModalOpen}
        isSmtpConfigured={isSmtpConfigured}
        onResetError={resetError}
      />
      {isModalOpen && (
        <EmailSelectionModalContainer
          title={formatMessage({ id: AppMessages['reports.permissionsSummary.title'] })}
          open={isEmailModalOpen}
          onClose={onEmailModalClose}
          onConfirm={handleConfirmEmails}
          hasNoDataError={hasNoDataError}
          onResetError={resetError}
          selectedUsers={selectedEmails}
        />
      )}
    </FormProvider>
  );
};
